// B10 — INVENTARIO DE TARJETAS PCF (retail-cards-business).
//
// El inventario se lleva POR CANTIDAD: las tarjetas llegan en sobres sellados y nadie en la tienda
// ve el plástico. La pantalla es un libro mayor — saldo arriba, movimientos abajo — más los lotes
// recibidos. No hay tabla de números ni buscador por número: ese dato no existe de este lado.
import { useState } from 'react';
import { CreditCard, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button, EmptyState, Loading, Select, type SelectOption } from '@/kit';
import type { CardBatchResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardBatchResponse';
import { ListFooterActions, ListToolbar, SectionHeader, SummaryStats } from '../components';
import { formatDateMx } from '@/lib/dateHelpers';
import { useRetailers, useStores } from '../negocios/api/useRetailOrg';
import {
    isStockNotFound,
    useCardBatches,
    useCardStock,
    useStockAlerts,
} from './api/useRetailCards';
import { CardMovementsTable, dedupeBySk } from './components/CardMovementsTable';
import { RegisterCardBatchModal, WriteOffCardsModal } from './components/CardStockModals';

export { dedupeBySk } from './components/CardMovementsTable';

const byLabel = (a: SelectOption, b: SelectOption): number =>
    a.label.localeCompare(b.label, 'es-MX');

export function TarjetasPage() {
    const [retailerId, setRetailerId] = useState('');
    const [storeId, setStoreId] = useState('');
    const { data: retailers, isLoading: loadingRetailers } = useRetailers();
    const { data: stores } = useStores(retailerId);

    const retailerOptions: SelectOption[] = (retailers ?? [])
        .map((r) => ({ value: r.retailerId, label: r.name }))
        .sort(byLabel);
    const storeOptions: SelectOption[] = (stores ?? [])
        .map((s) => ({ value: s.storeId, label: s.name }))
        .sort(byLabel);

    return (
        <div className="space-y-4">
            <SectionHeader title="INVENTARIO DE TARJETAS" divider />

            <div className="flex flex-wrap gap-3">
                <div className="w-64">
                    {loadingRetailers ? (
                        <Loading text="Cargando…" />
                    ) : (
                        <Select
                            label="Negocio"
                            value={retailerId}
                            onChange={(v) => {
                                setRetailerId(v);
                                setStoreId('');
                            }}
                            options={retailerOptions}
                            placeholder="Selecciona un negocio…"
                        />
                    )}
                </div>
                {retailerId ? (
                    <div className="w-64">
                        <Select
                            label="Tienda"
                            value={storeId}
                            onChange={setStoreId}
                            options={storeOptions}
                            placeholder="Selecciona una tienda…"
                        />
                    </div>
                ) : null}
            </div>

            {!retailerId || !storeId ? (
                <EmptyState
                    icon={<CreditCard className="size-8" aria-hidden />}
                    title="Elige negocio y tienda"
                    description="Selecciona un negocio y una tienda para ver su inventario de tarjetas PCF."
                />
            ) : (
                <StockPanel retailerId={retailerId} storeId={storeId} />
            )}
        </div>
    );
}

function StockPanel({ retailerId, storeId }: { retailerId: string; storeId: string }) {
    const [registerOpen, setRegisterOpen] = useState(false);
    const [writeOffOpen, setWriteOffOpen] = useState(false);
    const stock = useCardStock(retailerId, storeId);
    const batches = useCardBatches(retailerId, storeId);
    const alerts = useStockAlerts(retailerId);

    // Sin lote recibido no hay ítem de saldo: el silo responde 404 y eso es inventario en cero, no
    // una falla. El resto de la pantalla sigue de pie para poder registrar el primer lote.
    const noStockYet = isStockNotFound(stock.error);
    const stockFailed = stock.error !== null && !noStockYet;
    const balance = stock.data?.pages[0]?.balance;
    const available = balance?.available ?? 0;
    // Invalidar la infinite query refetchea todas las páginas con los cursores viejos: un asiento
    // nuevo arriba corre al último de la página 1 hacia la 2 y llegaría repetido.
    const movements = dedupeBySk(stock.data?.pages.flatMap((p) => p.movements) ?? []);

    return (
        <div className="space-y-3">
            {alerts.isError ? (
                // Una alarma que se apaga sola es peor que no tenerla: si no se pudo leer, se dice.
                <div className="rounded-lg border border-border px-4 py-2 text-[12px] text-muted-foreground">
                    No se pudo leer el estado de stock de las tiendas, así que esta pantalla no
                    puede avisarte si alguna está por quedarse sin tarjetas.
                </div>
            ) : alerts.data && alerts.data.alerts.length > 0 ? (
                <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-2 text-[12px] text-warning">
                    Stock bajo:{' '}
                    {alerts.data.alerts
                        .map(
                            (a) =>
                                `${a.storeName} (${String(a.availableCount)}/${String(a.threshold)})`,
                        )
                        .join(' · ')}
                </div>
            ) : null}

            {/* Con el saldo caído los contadores dirían 0/0/0 justo encima del aviso de error: dos
                afirmaciones contrarias, y la que se lee primero es la falsa. */}
            {stockFailed ? null : (
                <SummaryStats
                    stats={[
                        { label: 'Disponibles', value: available },
                        { label: 'Entregadas', value: balance?.deliveredTotal ?? 0 },
                        { label: 'Dadas de baja', value: balance?.writtenOffTotal ?? 0 },
                    ]}
                />
            )}

            <ListToolbar
                actions={
                    <>
                        <Button
                            leftIcon={<Plus aria-hidden />}
                            onClick={() => {
                                setRegisterOpen(true);
                            }}
                        >
                            Registrar entrada de lote
                        </Button>
                        <Button
                            variant="outline"
                            leftIcon={<Minus aria-hidden />}
                            disabled={available === 0}
                            onClick={() => {
                                setWriteOffOpen(true);
                            }}
                        >
                            Dar de baja
                        </Button>
                    </>
                }
            />

            <CardMovementsTable
                movements={movements}
                isLoading={stock.isLoading}
                error={noStockYet ? null : stock.error}
                onRetry={() => void stock.refetch()}
                hasMore={stock.hasNextPage}
                loadingMore={stock.isFetchingNextPage}
                onLoadMore={() => void stock.fetchNextPage()}
            />

            <ListFooterActions>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        toast.info('Exportar: próximamente');
                    }}
                >
                    Exportar
                </Button>
            </ListFooterActions>

            <BatchList batches={batches.data?.items ?? []} failed={batches.isError} />

            {registerOpen ? (
                <RegisterCardBatchModal
                    retailerId={retailerId}
                    storeId={storeId}
                    onClose={() => {
                        setRegisterOpen(false);
                    }}
                />
            ) : null}

            {writeOffOpen ? (
                <WriteOffCardsModal
                    retailerId={retailerId}
                    storeId={storeId}
                    availableIn={() => available}
                    onClose={() => {
                        setWriteOffOpen(false);
                    }}
                />
            ) : null}
        </div>
    );
}

function BatchList({ batches, failed }: { batches: CardBatchResponse[]; failed: boolean }) {
    if (!failed && batches.length === 0) return null;
    return (
        <div className="space-y-2">
            <p className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
                Lotes registrados
            </p>
            {/* Sin este aviso la sección desaparece y el operador cree que la tienda no recibió
                ningún lote, cuando lo que falló fue la lectura. */}
            {failed ? (
                <p className="rounded-lg border border-border px-4 py-2 text-[12px] text-muted-foreground">
                    No se pudieron leer los lotes de esta tienda.
                </p>
            ) : null}
            {batches.map((b) => (
                <div
                    key={b.batchSk}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-2"
                >
                    <span className="text-[12px]">
                        Remesa <span className="font-mono font-semibold">{b.supplierBatchRef}</span>
                        <span className="text-muted-foreground">
                            {' '}
                            · {String(b.quantity)} tarjetas · {formatDateMx(b.receivedAt)}
                        </span>
                    </span>
                    {b.notes ? (
                        <span className="text-[12px] text-muted-foreground">{b.notes}</span>
                    ) : null}
                </div>
            ))}
        </div>
    );
}
